import {
  Box3,
  BoxGeometry,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  OrthographicCamera,
  PerspectiveCamera,
  Quaternion,
  Vector3,
} from "three";
import { Engine } from "../engine/Engine";
import { getLocalBoundingBox } from "../util/GeometryUtil";
import { SimpleMeter } from "./SimpleMeter";
import { SugarModel } from "./SugarModel";
import { SugarModelConfigurator } from "./SugarModelConfigurator";
import { SugarModelJoint } from "./SugarModelJoint";

export class SugarModelDimensions {
  //private _configurator: SugarModelConfigurator;
  private _meters: SimpleMeter[] = [];
  private _show = false;

  constructor() {
    Engine.activeEngine?.addEventListener("camera-change", (e: any) => {
      this._align(e.detail);
    });
  }

  private _align(camera: OrthographicCamera | PerspectiveCamera) {
    this._meters.forEach((meter) => {
      const meterWorldPosition = meter.getWorldPosition(new Vector3());
      const meterDirection = meter.getWorldDirection(new Vector3());

      const meterWorldDirectionPosition = meterWorldPosition
        .clone()
        .add(meterDirection);

      camera.updateProjectionMatrix();
      camera.updateMatrixWorld();
      camera.updateWorldMatrix(true, true);

      const d = meterWorldDirectionPosition.project(camera);
      const p = meterWorldPosition.project(camera);

      const di = p.x > d.x ? 1 : -1;
      const sd0 = p.clone().sub(d).normalize().multiplyScalar(di);
      const rad = Math.atan2(
        sd0.y,
        sd0.x * (camera instanceof PerspectiveCamera ? camera.aspect : 1),
      );
      meter.labelSprite.material.rotation = rad;

      const zoom = camera.userData.controller.getDistance();
      //meter.visible = p.distanceTo(d) * zoom > .25;
      meter.setZoom(zoom);
    });
  }

  update(models: SugarModel[]) {
    const show = this._show;
    this.hide();
    if (show) {
      this.show(models);
    }
  }

  show(models: SugarModel[], showParts: boolean = true, outerSize?: Vector3) {
    this.hide();
    this._show = true;

    models.forEach((model) => {
      const { size, center, box } = this.getSize(model);

      let resolvedOuterSize: Vector3 | undefined = outerSize;
      if (!resolvedOuterSize && model instanceof SugarModelConfigurator) {
        const layout = this._layoutFromJoints(model);
        if (layout) {
          resolvedOuterSize = layout.size;
          if (this.debug) this._addDebugMeshes(model, layout.layouts);
          console.log(
            "[SugarModelDimensions] resolved outer size from joints",
            resolvedOuterSize,
          );
        }
      }

      if (model instanceof SugarModelConfigurator) {
        this._addMeterItemsOfConfigurator(
          model,
          center.y + size.y * 0.5 + 0.1,
          showParts,
        );
      }

      const marginSide = 0.15;
      const marginBck = 0.125;
      const marginTop = 0.2;

      const forward = new Vector3(0, 0, 1);
      const sizeLabel = resolvedOuterSize
        ? resolvedOuterSize.x
        : this._meters
            .filter(
              (m) =>
                m.attachTo == model &&
                Math.abs(m.direction.dot(forward)) < 0.01,
            )
            .reduce((acc, m) => acc + m.size, 0);
      const _meterX = new SimpleMeter(size.x);
      _meterX.setDirection(new Vector3(1, 0, 0), new Vector3(0, 1, 0));
      _meterX.setSize(size.x);
      _meterX.setSizeLabel(sizeLabel);
      _meterX.position.setX(center.x - size.x * 0.5);
      _meterX.position.setZ(center.z - size.z * 0.5 - marginBck);
      _meterX.position.setY(center.y + size.y * 0.5 + marginTop);
      model.add(_meterX);
      this._meters.push(_meterX);

      const sizeLabelY = this._meters
        .filter((m) => m.attachTo == model)
        .reduce((acc, m) => (acc > m.size ? acc : m.size), 0);
      const _meterY = new SimpleMeter(size.y);
      _meterY.setDirection(new Vector3(0, 1, 0), new Vector3(1, 0, 0));
      _meterY.setSize(size.y);
      //_meterY.setSizeLabel(sizeLabelY);
      _meterY.position.setX(center.x - size.x * 0.5 - marginSide);
      _meterY.position.setZ(center.z - size.z * 0.5 - marginSide);
      model.add(_meterY);
      this._meters.push(_meterY);

      //const nextJoint = model.joints?.find((j) => j.outType == "sag");
      //if (nextJoint && nextJoint.binded) return;

      const _meterZ = new SimpleMeter(size.z);
      const sizeLabelZ = resolvedOuterSize ? resolvedOuterSize.z : size.z;
      _meterZ.setDirection(new Vector3(0, 0, -1), new Vector3(0, 1, 0));
      _meterZ.setSize(size.z);
      _meterZ.setSizeLabel(sizeLabelZ);
      _meterZ.position.setX(center.x - size.x * 0.5 - marginSide);
      _meterZ.position.setZ(center.z + size.z * 0.5);

      model.add(_meterZ);
      this._meters.push(_meterZ);
      this._align(
        Engine.activeEngine!.activeCamera!.instance as
          | OrthographicCamera
          | PerspectiveCamera,
      );
    });

    Engine.activeEngine!.dispatchCustomEvent("request-update");
  }

  private _addMeterItemsOfConfigurator(
    configurator: SugarModelConfigurator,
    yOffset: number,
    showParts: boolean = true,
  ) {
    configurator.sideModels.forEach((m) => {
      const { size, center } = this.getSize(m);
      //console.log("side model size", size);
      const _meterM = new SimpleMeter(size.x, configurator);
      _meterM.setDirection(new Vector3(1, 0, 0), new Vector3(0, 1, 0));
      _meterM.setSize(size.x);
      _meterM.position.setX(center.x - size.x * 0.5);
      _meterM.position.setZ(center.z - size.z * 0.5 - 0.02);
      _meterM.position.setY(center.y + size.y * 0.5 + 0.1);
      /*m.add(_meterM);
      m.updateWorldMatrix(true, true);
      model.attach(_meterM);
      _meterM.position.y = yOffset; // Adjust position for configurator models*/
      this._meters.push(_meterM);
    });

    configurator.models.forEach((model) => {
      const { size, center } = this.getSize(model);

      const _meterM = new SimpleMeter(size.x, configurator);
      _meterM.setDirection(new Vector3(1, 0, 0), new Vector3(0, 1, 0));
      _meterM.setSize(size.x);
      _meterM.position.setX(center.x - size.x * 0.5);
      _meterM.position.setZ(center.z - size.z * 0.5 - 0.02);
      _meterM.position.setY(center.y + size.y * 0.5 + 0.1);

      if (showParts) {
        model.add(_meterM);
        model.updateWorldMatrix(true, true);
        configurator.attach(_meterM);
      }
      _meterM.position.y = yOffset; // Adjust position for configurator models
      this._meters.push(_meterM);

      if (model.data?.id == 60344 || model.data?.id == 87826) {
        const _meterZ = new SimpleMeter(size.z);
        _meterZ.setDirection(new Vector3(0, 0, -1), new Vector3(0, 1, 0));
        _meterZ.setSize(size.z);
        _meterZ.position.setX(center.x + size.x * 0.5 + 0.2);
        _meterZ.position.setZ(center.z + size.z * 0.5);

        if (showParts) {
          model.add(_meterZ);
          model.updateWorldMatrix(true, true);
          configurator.attach(_meterZ);
        }
        _meterZ.position.y = yOffset; // Adjust position for configurator models
        this._meters.push(_meterZ);
      }
    });
  }

  private _debugMeshes: Mesh[] = [];
  public debug = true;

  private _layoutFromJoints(configurator: SugarModelConfigurator):
    | {
        size: Vector3;
        layouts: {
          model: SugarModel;
          pos: Vector3;
          rot: Quaternion;
          localBox: Box3;
        }[];
      }
    | undefined {
    const all: SugarModel[] = [
      ...configurator.models,
      ...configurator.sideModels,
    ];
    if (all.length === 0) return undefined;

    const dataSize = (m: SugarModel): Vector3 | undefined => {
      const dx = m.data?.x;
      const dy = m.data?.y;
      const dz = m.data?.z;
      if (
        typeof dx !== "number" ||
        dx <= 0 ||
        typeof dy !== "number" ||
        dy <= 0 ||
        typeof dz !== "number" ||
        dz <= 0
      )
        return undefined;
      return new Vector3(dx / 100, dy / 100, dz / 100);
    };

    const dataLocalBox = (m: SugarModel): Box3 | undefined => {
      const s = dataSize(m);
      if (!s) return undefined;
      // Y origin at bottom (matches getSize and joint apply convention).
      return new Box3(
        new Vector3(-s.x / 2, 0, -s.z / 2),
        new Vector3(s.x / 2, s.y, s.z / 2),
      );
    };

    const jointAnchor = (j: SugarModelJoint): Vector3 => {
      if (
        Object.prototype.hasOwnProperty.call(
          j.userData?.info ?? {},
          "direction",
        )
      ) {
        const dims = j.userData.info.direction
          ?.split(" ")
          .map((n: string) => parseFloat(n));
        console.log(
          "[SugarModelDimensions] using joint anchor info from userData",
          j.userData.info.direction,
        );

        const v = new Vector3(dims[0], dims[1], dims[2]);

        console.log("original v", v);
        return v;
      }

      console.log(
        "[SugarModelDimensions] no joint anchor info for joint",
        j.inType,
      );
      if (j.inType === "sag") return new Vector3(1, 0, -1);
      if (j.inType === "sol") return new Vector3(-1, 0, -1);
      if (j.inType === "arka") return new Vector3(0, 0, -1);
      if (j.inType === "on") return new Vector3(0, 0, 1);
      if (j.inType === "top") return new Vector3(0, 1, 0);
      if (j.inType === "bottom") return new Vector3(0, 0, 0);
      return new Vector3();
    };

    // Keep joint alignment in the same coordinate system as dataLocalBox.
    // j.position is derived from the loaded mesh OBB, while these layout
    // boxes are derived from data.x/y/z.
    const jointLocalPos = (
      j: SugarModelJoint,
      m: SugarModel,
    ): Vector3 | undefined => {
      const s = dataSize(m);
      if (!s) return undefined;
      const anchor = jointAnchor(j);
      const offset = j.offset?.clone() ?? new Vector3();
      console.log("size", s, "anchor", anchor);
      return new Vector3(s.x * anchor.x * 0.5, s.y, s.z * anchor.z * 0.5);
    };

    const seed = all[0];
    if (!dataLocalBox(seed)) {
      console.log("[SugarModelDimensions] seed has no usable box — abort");
      return undefined;
    }

    configurator.updateMatrixWorld(true);
    const cInv = new Matrix4().copy(configurator.matrixWorld).invert();

    const localPoseOf = (m: SugarModel): { pos: Vector3; rot: Quaternion } => {
      m.updateMatrixWorld(true);
      const mat = new Matrix4().multiplyMatrices(cInv, m.matrixWorld);
      const pos = new Vector3();
      const rot = new Quaternion();
      const scl = new Vector3();
      mat.decompose(pos, rot, scl);
      return { pos, rot };
    };

    const positions = new Map<SugarModel, Vector3>();
    const rotations = new Map<SugarModel, Quaternion>();

    const runBFS = (start: SugarModel) => {
      const queue: SugarModel[] = [start];
      while (queue.length > 0) {
        const model = queue.shift()!;
        const mPos = positions.get(model)!;
        const mRot = rotations.get(model)!;

        const joints = model.joints ?? [];
        for (const j of joints) {
          if (!j.binded) continue;
          const jOther = j.bindedJoint;
          if (!jOther) continue;
          const other = jOther.model;
          if (!other || positions.has(other)) continue;
          if (!dataLocalBox(other)) continue;

          const selfJointLocal = jointLocalPos(j, model);
          const otherJointLocal = jointLocalPos(jOther, other);
          if (!selfJointLocal || !otherJointLocal) continue;

          const selfJointPos = selfJointLocal
            .clone()
            .applyQuaternion(mRot)
            .add(mPos);
          const selfJointDir = j.direction
            .clone()
            .applyQuaternion(mRot)
            .normalize();

          const otherTargetDir = selfJointDir.clone().negate();
          const otherJointDirLocal = jOther.direction.clone().normalize();
          const otherRot = new Quaternion().setFromUnitVectors(
            otherJointDirLocal,
            otherTargetDir,
          );

          const otherJointOffsetWorld = otherJointLocal
            .clone()
            .applyQuaternion(otherRot);
          const otherPos = selfJointPos.clone().sub(otherJointOffsetWorld);

          positions.set(other, otherPos);
          rotations.set(other, otherRot);
          queue.push(other);
        }
      }
    };

    // Seed the chain with models[0]'s real configurator-local pose so debug
    // meshes line up with the actual scene. Then iterate over every child;
    // any model not reached via joint chain (typically standalone side
    // models) gets its own configurator-local pose as a fresh BFS seed.
    const seedPose = localPoseOf(seed);
    positions.set(seed, seedPose.pos);
    rotations.set(seed, seedPose.rot);
    runBFS(seed);

    for (const m of all) {
      if (positions.has(m)) continue;
      if (!dataLocalBox(m)) continue;
      const pose = localPoseOf(m);
      positions.set(m, pose.pos);
      rotations.set(m, pose.rot);
      runBFS(m);
    }

    const union = new Box3();
    union.makeEmpty();
    const layouts: {
      model: SugarModel;
      pos: Vector3;
      rot: Quaternion;
      localBox: Box3;
    }[] = [];
    for (const [model, pos] of positions) {
      const localBox = dataLocalBox(model);
      if (!localBox) continue;
      const rot = rotations.get(model)!;
      const mat = new Matrix4().compose(pos, rot, new Vector3(1, 1, 1));
      const transformed = localBox.clone().applyMatrix4(mat);
      union.union(transformed);
      layouts.push({ model, pos, rot, localBox });
    }

    if (union.isEmpty()) return undefined;
    const rawSize = union.getSize(new Vector3());
    // Snap to centimeter precision so float drift from joint.position
    // (computed via localObb.halfSize) doesn't show up in the cm label.
    const size = new Vector3(
      Math.round(rawSize.x * 100) / 100,
      Math.round(rawSize.y * 100) / 100,
      Math.round(rawSize.z * 100) / 100,
    );
    return { size, layouts };
  }

  private _addDebugMeshes(
    configurator: SugarModelConfigurator,
    layouts: {
      model: SugarModel;
      pos: Vector3;
      rot: Quaternion;
      localBox: Box3;
    }[],
  ) {
    let added = 0;
    for (const { pos, rot, localBox } of layouts) {
      const size = new Vector3();
      const localCenter = new Vector3();
      localBox.getSize(size);
      localBox.getCenter(localCenter);
      if (size.x <= 0 || size.y <= 0 || size.z <= 0) continue;
      const mesh = new Mesh(
        new BoxGeometry(size.x, size.y, size.z),
        new MeshBasicMaterial({
          color: 0x00ff00,
          transparent: true,
          opacity: 0.35,
          depthTest: false,
          depthWrite: false,
        }),
      );
      mesh.renderOrder = 999;
      mesh.frustumCulled = false;
      // BoxGeometry is centered on its origin; shift to localBox center in
      // model-local space, then transform to configurator-local space.
      const worldCenter = localCenter.clone().applyQuaternion(rot).add(pos);
      mesh.position.copy(worldCenter);
      mesh.quaternion.copy(rot);
      configurator.add(mesh);
      this._debugMeshes.push(mesh);
      added++;
    }
  }

  private _clearDebugMeshes() {
    for (const m of this._debugMeshes) {
      m.removeFromParent();
      (m.material as MeshBasicMaterial).dispose();
      m.geometry.dispose();
    }
    this._debugMeshes = [];
  }

  hide() {
    this._show = false;
    this._meters.forEach((m) => {
      m.dispose();
      m.removeFromParent();
    });

    this._meters = [];
    this._clearDebugMeshes();

    Engine.activeEngine!.dispatchCustomEvent("request-update");
  }

  toggle(models: SugarModel[]) {
    if (!this._show) {
      this.show(models);
    } else {
      this.hide();
    }
  }

  getSize(model: SugarModel) {
    const bound = getLocalBoundingBox(model);

    const size = new Vector3();
    bound?.getSize(size);
    const center = new Vector3();
    bound?.getCenter(center);

    const box = new Mesh(
      new BoxGeometry(size.x, size.y, size.z),
      new MeshBasicMaterial({
        color: 0xff0000,
        transparent: true,
        opacity: 0.8,
      }),
    );
    box.position.copy(center);

    if (model.data?.x) {
      size.setX(model.data.x / 100);
      center.setX(0);
    }
    if (model.data?.y) {
      size.setY(model.data.y / 100);
      center.setY(model.data.y / 200); // Adjust center for y
    }
    if (model.data?.z) {
      size.setZ(model.data.z / 100);
      center.setZ(0);
    }

    return { size, center, box };
  }
}
